import React, { useEffect, useMemo, useState } from 'react';
import { Calculator, Save } from 'lucide-react';
import { scheduleExpectedCountApi } from '../services/api';
import { Schedule, ScheduleDayCounts } from '../types';
import { EXPECTED_COUNT_DAY_LABELS, emptyDayCounts, normalizeDayCounts } from '../utils/scheduleExpectedCounts';

interface ScheduleExpectedCountsPanelProps {
  schedules: Schedule[];
}

export default function ScheduleExpectedCountsPanel({ schedules }: ScheduleExpectedCountsPanelProps) {
  const allSchedules = useMemo(
    () => [...schedules].sort((a, b) => a.name.localeCompare(b.name)),
    [schedules]
  );
  const [scheduleId, setScheduleId] = useState<number>(allSchedules[0]?.id || 0);
  const [counts, setCounts] = useState<ScheduleDayCounts>(emptyDayCounts());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!scheduleId && allSchedules[0]?.id) {
      setScheduleId(allSchedules[0].id);
    }
  }, [allSchedules, scheduleId]);

  useEffect(() => {
    if (!scheduleId) {
      setCounts(emptyDayCounts());
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await scheduleExpectedCountApi.get(scheduleId);
        if (!cancelled) setCounts(normalizeDayCounts(data));
      } catch (error) {
        console.error('Error loading expected counts:', error);
        if (!cancelled) {
          alert('Failed to load expected counts');
          setCounts(emptyDayCounts());
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [scheduleId]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!scheduleId) {
      alert('Please select a schedule.');
      return;
    }
    setSaving(true);
    try {
      await scheduleExpectedCountApi.upsert(scheduleId, { scheduleId, ...counts });
      alert('Expected counts saved.');
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to save expected counts';
      alert(typeof message === 'string' ? message : 'Failed to save expected counts');
    } finally {
      setSaving(false);
    }
  };

  if (allSchedules.length === 0) {
    return (
      <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center">
        <Calculator className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-neutral-900 mb-2">No schedules found</h3>
        <p className="text-neutral-600">Create a schedule first, then set Monday–Sunday and BH expected counts.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold">Expected Counts</h2>
          <p className="text-sm md:text-base text-neutral-600">
            Set the expected shift count for Monday through Sunday, plus BH (bank holiday). Scheduling uses these
            defaults every week unless a week is overridden.
          </p>
        </div>
      </div>

      <form onSubmit={handleSave}>
        <div className="mb-4">
          <label className="block text-sm font-medium text-neutral-700 mb-1">Schedule</label>
          <select
            value={scheduleId}
            onChange={(e) => setScheduleId(Number(e.target.value))}
            className="w-full sm:w-80 px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {allSchedules.map((schedule) => (
              <option key={schedule.id} value={schedule.id}>
                {schedule.name}
                {!schedule.isActive ? ' (Inactive)' : ''}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="py-12 text-center text-neutral-600">Loading expected counts...</div>
        ) : (
          <>
            <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-neutral-700 w-36">Day</th>
                    <th className="text-left p-4 text-sm font-medium text-neutral-700">Expected count</th>
                  </tr>
                </thead>
                <tbody>
                  {EXPECTED_COUNT_DAY_LABELS.map((day) => (
                    <tr key={day.key} className="border-b border-neutral-100">
                      <td className="p-4 font-medium text-neutral-900">{day.label}</td>
                      <td className="p-4">
                        <input
                          type="number"
                          min={0}
                          value={counts[day.key]}
                          onChange={(e) =>
                            setCounts((prev) => ({
                              ...prev,
                              [day.key]: Math.max(0, Number(e.target.value) || 0),
                            }))
                          }
                          className="w-32 px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : 'Save expected counts'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
